import { faDatabase, faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import { StyledInputLabel } from "components/Form/FormComponents/FormSwitch/FormSwitch.styled"
import { RouterPrompt } from "components/RouterPrompt"
import theme from "css/theme/light"
import { useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { useContext, useEffect, useRef, useState } from "react"
import { StyledListItem } from "shared/styled/General"
import * as Yup from "yup"
import { Sidebar } from "components/sidedrawer/Sidebar2"
import { t } from "i18next"

const validationSchema = Yup.object().shape({
  name: Yup.string().required(t("global.name_is_required", "Name is required")),
  type: Yup.string().required(t("global.type_is_required", "Type is required")),
  active: Yup.bool(),
  notes: Yup.string().nullable().max(200, t("global.description_can_only_200", "")),
  dateId: Yup.string().when("isReminderDate", {
    is: true,
    then: (schema) => schema.required(t("global.you_must_specify_field", "Must specify field")),
    otherwise: (schema) => schema.nullable()
  }),
  timeInterval: Yup.number().when("isReminderDate", {
    is: true,
    then: (schema) =>
      schema
        .integer(t("global.must_be_a_integer", "Must be a integer"))
        .min(1, t("vehicleregistry.number_of_days_must_be_1", "Number of days must be at least 1"))
        .required(t("global.days_is_required", "Days is required")),
    otherwise: (schema) => schema.nullable()
  }),
  numberId: Yup.string().when("isReminderNumber", {
    is: true,
    then: (schema) => schema.required(t("global.you_must_specify_field", "Must specify field")),
    otherwise: (schema) => schema.nullable()
  }),
  numberInterval: Yup.number().when("isReminderNumber", {
    is: true,
    then: (schema) =>
      schema
        .integer(t("global.must_be_a_integer", "Must be a integer"))
        .min(1, t("vehicleregistry.number_value_must_be_at_least_1", "Number value must be at least 1"))
        .required(t("vehicleregistry.number_value_is_required", "Number value is required")),
    otherwise: (schema) => schema.nullable()
  })
})

const NameTextField = () => {
  const { errors, handleBlur, handleChange, touched, values } = useFormikContext()

  return (
    <StyledListItem>
      <StyledInputLabel>
        <Typography variant="h6" color="text.primary">
          {t("global.name", "Name")}
        </Typography>
      </StyledInputLabel>

      <TextField
        autoComplete="off"
        fullWidth
        id="name"
        value={values.name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.name && errors.name ? true : false}
        helperText={touched.name && errors.name ? t("global.name_is_required", "Name is required") : null}
        variant="outlined"
      />
    </StyledListItem>
  )
}

const NoteTextField = () => {
  const { errors, handleBlur, handleChange, touched, values } = useFormikContext()

  return (
    <StyledListItem>
      <StyledInputLabel>
        <Typography variant="h6" color="text.primary">
          {t("global.listing", "Listing")}
        </Typography>
      </StyledInputLabel>
      <TextField
        multiline={true}
        rows={3}
        autoComplete="off"
        fullWidth
        id="notes"
        value={values.notes}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.notes && errors.notes ? true : false}
        helperText={touched.notes && errors.notes ? errors.notes : null}
        variant="outlined"
      />
    </StyledListItem>
  )
}

const MandatorySwitch = () => {
  const { setFieldValue, values } = useFormikContext()

  return (
    <StyledListItem>
      <FormControlLabel
        label={t("global.mandatory", "Mandatory")}
        control={
          <Switch
            disabled={values.type === "Boolean"}
            onChange={(item) => {
              const checked = item.target.checked
              setFieldValue("isRequired", checked)
            }}
            checked={values.isRequired}
          />
        }
      />
    </StyledListItem>
  )
}

const ActiveSwitch = () => {
  const { setFieldValue, values } = useFormikContext()

  return (
    <StyledListItem sx={{ minHeight: "auto !important" }}>
      <FormControlLabel
        label={t("global.active", "Active")}
        control={
          <Switch
            onChange={(item) => {
              const checked = item.target.checked
              setFieldValue("active", checked)
            }}
            checked={values.active}
          />
        }
      />
    </StyledListItem>
  )
}

const CategoryDropdown = () => {
  const { values, setFieldValue, handleChange, handleBlur, touched, errors } = useFormikContext()

  const categories = [
    { name: t("global.none", "None") },
    { name: t("global.service", "Service") },
    { name: t("global.inspection", "Inspection") }
  ]

  return (
    <StyledListItem sx={{ width: "100%" }}>
      <StyledInputLabel>
        <Typography variant="h6" color="text.primary">
          {t("global.category", "Category")}
        </Typography>
      </StyledInputLabel>

      <Autocomplete
        disablePortal
        fullWidth
        id="category"
        options={categories}
        getOptionLabel={(option) => (option.name ? option.name : "")}
        value={categories.find((i) => i.name === values.category) || ""}
        getOptionSelected={(option, value) => {
          return option.name === value.name
        }}
        onChange={(e, value) => {
          setFieldValue("category", value?.name)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            name="category"
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.category && errors.category ? true : false}
            helperText={touched.category && errors.category ? errors.category : null}
          />
        )}
      />
    </StyledListItem>
  )
}

const TypeDropdown = ({ editMode }) => {
  const { values, setFieldValue, handleChange, handleBlur, touched, errors } = useFormikContext()

  const customFieldTypes = [
    {
      name: t("global.select_type", "Select type"),
      id: ""
    },
    {
      name: t("global.number", "Number"),
      id: "Numeric"
    },
    {
      name: t("global.date", "Date"),
      id: "Date"
    },
    {
      name: t("global.textfield", "Textfield"),
      id: "Text"
    },
    {
      name: t("global.yes_no", "Yes/No"),
      id: "Boolean"
    },
    {
      name: t("global.reminder", "Reminder"),
      id: "Notification"
    },
    {
      name: t("global.recurring_reminder", "Recurring reminder"),
      id: "RepeatedNotification"
    }
  ]

  return (
    <StyledListItem sx={{ width: "100%" }}>
      <StyledInputLabel>
        <Typography variant="h6" color="text.primary">
          {t("global.type", "Type")}
        </Typography>
      </StyledInputLabel>

      <Autocomplete
        fullWidth
        disablePortal
        disabled={editMode}
        id="status"
        options={customFieldTypes}
        getOptionLabel={(option) => (option.name ? option.name : "")}
        value={customFieldTypes.find((i) => i.id === values.type) || ""}
        getOptionSelected={(option, value) => {
          return option.id === value.id
        }}
        onChange={(e, value) => {
          setFieldValue("type", value?.id)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            name="type"
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.type && errors.type ? true : false}
            helperText={touched.type && errors.type ? t("global.type_is_required", "Type is required") : null}
          />
        )}
      />
    </StyledListItem>
  )
}

const TriggerSetting = ({ checkboxId, dropdownId, reminderInterval, triggerFieldList }) => {
  const { values, setFieldValue, handleChange, handleBlur, touched, errors } = useFormikContext()

  const dropdownTitle =
    checkboxId === "isReminderDate" ? t("global.date", "Date") : t("global.number", "Number")
  const unitSuffix = checkboxId === "isReminderDate" ? t("global.days", "days") : ""

  return (
    <StyledListItem>
      <Box sx={{ display: "flex", alignItems: "end", marginLeft: "-10px" }}>
        <Checkbox id={checkboxId} checked={values[checkboxId]} onChange={handleChange} color="primary" />
        <ListItemText style={{ paddingBottom: "8px" }} primary={dropdownTitle} />
      </Box>

      <Autocomplete
        sx={{ width: "100%" }}
        disabled={!values[checkboxId]}
        style={{ background: theme.palette.background.paper }}
        id={dropdownId}
        options={triggerFieldList}
        getOptionLabel={(option) => (option.name ? option.name : "")}
        value={triggerFieldList.find((i) => i.id === values[dropdownId]) || ""}
        getOptionSelected={(option, value) => {
          return option.id === value.id
        }}
        onChange={(e, value) => {
          setFieldValue(dropdownId, value?.id)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            name={dropdownId}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched[dropdownId] && errors[dropdownId] ? true : false}
            helperText={touched[dropdownId] && errors[dropdownId] ? errors[dropdownId] : null}
          />
        )}
      />
      <ListItemText primary={t("vehicleregistry.number", "Number")} style={{ paddingTop: "4px" }} />
      <Box sx={{ display: "flex", alignItems: "baseline" }}>
        <TextField
          disabled={!values[checkboxId]}
          autoComplete="off"
          id={reminderInterval}
          type="number"
          value={values[reminderInterval]}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched[reminderInterval] && errors[reminderInterval] ? true : false}
          helperText={touched[reminderInterval] && errors[reminderInterval] ? errors[reminderInterval] : null}
          variant="outlined"
          sx={{
            backgroundColor: "white",
            width: "30%",
            "& .MuiInputBase-root": {
              height: "36px"
            }
          }}
        />
        <Typography sx={{ paddingLeft: "8px", color: "grey" }}>{unitSuffix}</Typography>
      </Box>
    </StyledListItem>
  )
}

const NotificationForm = ({ usersResult, selectedReceivers, setSelectedReceiver, removeReceiver }) => {
  const { values, setFieldValue, handleChange, handleBlur, touched, errors } = useFormikContext()
  const [dateFieldsResult, dateFieldApi] = useApi()
  const [numberFieldsResult, numberFieldApi] = useApi()
  const [dateFields, setDateFields] = useState([])
  const [numberFields, setNumberFields] = useState([])
  const categories = {
    Besiktning: 0,
    Service: 1
  }

  useEffect(() => dateFieldApi.get(apiEndpoints.vehicleregistryadmin.getdatetimefields), [])
  useEffect(() => numberFieldApi.get(apiEndpoints.vehicleregistryadmin.getnumberfields), [])

  useEffect(() => {
    if (dateFieldsResult.status !== 2) return
    setDateFields(dateFieldsResult.data)
  }, [dateFieldsResult, values.category])

  useEffect(() => {
    if (numberFieldsResult.status !== 2) return
    if (values.category === "Service") {
      setNumberFields(
        numberFieldsResult.data.filter((numberField) => numberField.category === categories["Service"])
      )
    } else {
      setNumberFields(numberFieldsResult.data)
    }
  }, [numberFieldsResult, values.category])

  return (
    <StyledListItem>
      <Box bgcolor={theme.palette.grey[100]} padding={2} width="100%">
        <Typography variant="h6">{t("vehicleregistry.trigger_settings", "Trigger settings")}</Typography>
        <TriggerSetting
          checkboxId="isReminderDate"
          dropdownId="dateId"
          reminderInterval="timeInterval"
          triggerFieldList={dateFields}
        />
        {values.category === "Service" && (
          <TriggerSetting
            checkboxId="isReminderNumber"
            dropdownId="numberId"
            reminderInterval="numberInterval"
            triggerFieldList={numberFields}
          />
        )}
        <ListItem>
          <FormControlLabel
            label={t("vehicleregistry.show_in_calendar", "Show in calendar")}
            control={
              <Switch
                onChange={(item) => {
                  const checked = item.target.checked
                  setFieldValue("showInCalendar", checked)
                }}
                checked={values.showInCalendar}
              />
            }
          />
        </ListItem>
        <ListItem>
          <FormControlLabel
            label={t("global.send_email_notification", "Send email notification")}
            control={
              <Switch
                onChange={(item) => {
                  const checked = item.target.checked
                  setFieldValue("sendNotification", checked)
                }}
                checked={values.sendNotification}
              />
            }
          />
        </ListItem>
        {values.sendNotification && (
          <>
            <Box>
              <ListItemText
                primary={t("vehicleregistry.add_recipients", "Add recipients")}
                style={{ paddingBottom: "8px", marginTop: 5 }}
              />
            </Box>
            <ListItem
              style={{
                marginBottom: 8,
                border: `1px solid ${theme.palette.grey["100"]}`,
                borderRadius: 4
              }}>
              <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={11}>
                  {usersResult.status === 2 && (
                    <Autocomplete
                      style={{ minWidth: 250, background: "white" }}
                      size="small"
                      id="reciever"
                      options={usersResult.data}
                      getOptionDisabled={(option) =>
                        selectedReceivers.findIndex((x) => x.userId === option.userId) !== -1
                      }
                      getOptionLabel={(option) => (option.name ? option.name : null)}
                      getOptionSelected={(option, value) => {
                        return option.userId === value.userId
                      }}
                      onChange={(e, value) => {
                        if (value !== null) {
                          setSelectedReceiver((prev) => [...prev, value])
                        }
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Stack direction="row" spacing={2} justifyContent="space-between">
                            <Box sx={{ fontWeight: 600, minWidth: "100px" }}>{option.name}</Box>
                            <Box sx={{ opacity: 0.6 }}>{option.email}</Box>
                          </Stack>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      )}
                    />
                  )}
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    onClick={() => {
                      setFieldValue("recipients", selectedReceivers)
                    }}
                    color="primary">
                    <FontAwesomeIcon icon={faPlus} />
                  </IconButton>
                </Grid>{" "}
              </Grid>
            </ListItem>
            {values?.recipients?.length > 0 && (
              <>
                <ListItem
                  style={{
                    marginBottom: 8,
                    border: `1px solid ${theme.palette.grey["100"]}`,
                    borderRadius: 4
                  }}>
                  <Box gap={2} borderColor={theme.palette.grey.light} width="100%">
                    {values?.recipients?.map((data, index) => (
                      <Grid
                        key={index}
                        borderBottom={selectedReceivers.length - 1 !== index ? 1 : 0}
                        borderColor={theme.palette.grey.light}
                        mb={selectedReceivers.length - 1 !== index ? 1 : 0}
                        pb={1}
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center">
                        <Grid item xs={11}>
                          <Typography>{data.name}</Typography>
                          <Typography variant="bodySmall">{data.email}</Typography>
                        </Grid>
                        <Grid item xs={1}>
                          <IconButton
                            size="small"
                            onClick={() => removeReceiver(setFieldValue, values, index)}
                            color="primary">
                            <FontAwesomeIcon variant="contained" icon={faTrash} />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                </ListItem>{" "}
              </>
            )}
          </>
        )}
      </Box>
    </StyledListItem>
  )
}

const DateForm = ({ usersResult, selectedReceivers, setSelectedReceiver, removeReceiver }) => {
  const { values, setFieldValue, handleChange, handleBlur } = useFormikContext()

  return (
    <StyledListItem>
      <FormControlLabel
        label={t("global.send_email_notification", "Send email notification")}
        control={
          <Switch
            onChange={(item) => {
              const checked = item.target.checked
              setFieldValue("sendNotification", checked)
            }}
            checked={values.sendNotification}
          />
        }
      />

      {values.sendNotification && (
        <Box bgcolor={theme.palette.grey[100]} padding={2} width="100%">
          <StyledListItem>
            <ListItemText
              primary={t("vehicleregistry.add_recipients", "Add recipients")}
              style={{ paddingBottom: "8px", marginTop: 5 }}
            />
          </StyledListItem>
          <ListItem
            style={{
              marginBottom: 8,
              border: `1px solid ${theme.palette.grey["100"]}`,
              borderRadius: 4
            }}>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              <Grid item xs={11}>
                {usersResult.status === 2 && (
                  <Autocomplete
                    style={{ minWidth: 250, background: "white" }}
                    size="small"
                    id="reciever"
                    options={usersResult.data}
                    getOptionDisabled={(option) =>
                      selectedReceivers.findIndex((x) => x.userId === option.userId) !== -1
                    }
                    getOptionLabel={(option) => (option.name ? option.name : null)}
                    getOptionSelected={(option, value) => {
                      return option.userId === value.userId
                    }}
                    onChange={(e, value) => {
                      if (value !== null) {
                        setSelectedReceiver((prev) => [...prev, value])
                      }
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Stack direction="row" spacing={2} justifyContent="space-between">
                          <Box sx={{ fontWeight: 600, minWidth: "100px" }}>{option.name}</Box>
                          <Box sx={{ opacity: 0.6 }}>{option.email}</Box>
                        </Stack>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" onChange={handleChange} onBlur={handleBlur} />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={1}>
                <IconButton
                  onClick={() => {
                    setFieldValue("recipients", selectedReceivers)
                  }}
                  color="primary">
                  <FontAwesomeIcon icon={faPlus} />
                </IconButton>
              </Grid>{" "}
            </Grid>
          </ListItem>
          {values?.recipients?.length > 0 && (
            <>
              <ListItem
                style={{
                  marginBottom: 8,
                  border: `1px solid ${theme.palette.grey["100"]}`,
                  borderRadius: 4
                }}>
                <Box gap={2} borderColor={theme.palette.grey.light} width="100%">
                  {values?.recipients?.map((data, index) => (
                    <Grid
                      key={index}
                      borderBottom={selectedReceivers.length - 1 !== index ? 1 : 0}
                      borderColor={theme.palette.grey.light}
                      mb={selectedReceivers.length - 1 !== index ? 1 : 0}
                      pb={1}
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center">
                      <Grid item xs={11}>
                        <Typography>{data.name}</Typography>
                        <Typography variant="bodySmall">{data.email}</Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          size="small"
                          onClick={() => removeReceiver(setFieldValue, values, index)}
                          color="primary">
                          <FontAwesomeIcon variant="contained" icon={faTrash} />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              </ListItem>
            </>
          )}
        </Box>
      )}
    </StyledListItem>
  )
}

const IntervalTextField = () => {
  const { values, handleChange, touched, handleBlur, errors } = useFormikContext()
  const [categoryType, setCategoryType] = useState()
  const [unitDescription, setUnitDescription] = useState()

  useEffect(() => {
    let categoryType
    if (values.type === "Numeric") {
      categoryType = "numberInterval"
      setUnitDescription(t("vehicleregistry.service_interval", "Service interval"))
    } else {
      categoryType = "timeInterval"
      setUnitDescription(t("vehicleregistry.days_between_service", "Days between service"))
    }
    setCategoryType(categoryType)
  }, [values.category, values.type])

  return (
    <StyledListItem>
      <TextField
        autoComplete="off"
        id={categoryType}
        type="number"
        value={values[categoryType]}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched[categoryType] && errors[categoryType] ? true : false}
        helperText={touched[categoryType] && errors[categoryType] ? errors[categoryType] : null}
        variant="outlined"
        sx={{
          backgroundColor: "white",
          width: "30%",
          "& .MuiInputBase-root": {
            height: "36px"
          }
        }}
      />
      <Typography sx={{ paddingLeft: "8px", color: "grey" }}>{unitDescription}</Typography>
    </StyledListItem>
  )
}

const LastStep = ({ usersResult, selectedReceivers, setSelectedReceiver, removeReceiver }) => {
  const { values } = useFormikContext()

  return (
    <>
      {values.category === "Service" && (values.type === "Numeric" || values.type === "Date") && (
        <IntervalTextField />
      )}
      {(values.type === "RepeatedNotification" || values.type === "Notification") && (
        <NotificationForm
          usersResult={usersResult}
          selectedReceivers={selectedReceivers}
          setSelectedReceiver={setSelectedReceiver}
          removeReceiver={removeReceiver}
        />
      )}
      {values.type === "Date" && (
        <DateForm
          usersResult={usersResult}
          selectedReceivers={selectedReceivers}
          setSelectedReceiver={setSelectedReceiver}
          removeReceiver={removeReceiver}
        />
      )}
    </>
  )
}

const CustomFieldForm = (props) => {
  const { editItemResult, addResult, updateResult, editMode, onAddSubmit, onUpdateSubmit } = props
  const appContext = useContext(AppContext)
  const [usersResult, getUsers] = useApi()
  const formRef = useRef()

  useEffect(() => {
    getUsers.get(apiEndpoints.vehicleregistryadmin.getuserstoassign)
  }, [])

  const initialValues =
    editMode && editItemResult.status === 2
      ? {
          ...editItemResult.data,
          timeInterval: editItemResult.data?.timeInterval ? Math.abs(editItemResult.data.timeInterval) : null,
          numberInterval: editItemResult.data?.numberInterval
            ? Math.abs(editItemResult.data.numberInterval)
            : null,
          timeIntervalBeforeAfter: editItemResult.data.timeInterval > 0 ? "after" : "before",
          numberIntervalBeforeAfter: editItemResult.data.numberInterval > 0 ? "after" : "before",
          isReminderDate: editItemResult.data.dateId && editItemResult.data.timeInterval ? true : false,
          isReminderNumber: editItemResult.data.numberId && editItemResult.data.numberInterval ? true : false
        }
      : {
          id: 0,
          name: "",
          type: "",
          active: true,
          isRequired: false,
          sendNotification: false,
          notes: "",
          recipients: [],
          isReminderDate: false,
          timeInterval: 0,
          timeIntervalBeforeAfter: "before",
          isReminderNumber: false,
          numberInterval: 0,
          numberIntervalBeforeAfter: "before"
        }

  const handleSubmit = (values) => {
    let filteredValues = { ...values }
    switch (values.type) {
      case "Numeric":
        delete filteredValues.timeInterval
        break
      case "Date":
        delete filteredValues.numberInterval
        break
      case "Notification":
      case "RepeatedNotification":
        if (!values.isReminderDate) {
          delete filteredValues.dateId
          delete filteredValues.timeInterval
        }
        if (!values.isReminderNumber) {
          delete filteredValues.numberId
          delete filteredValues.numberInterval
        }
        if (values.numberInterval && values.numberIntervalBeforeAfter === "before") {
          filteredValues.numberInterval = -filteredValues.numberInterval
        }
        if (values.timeInterval && values.timeIntervalBeforeAfter === "before") {
          filteredValues.timeInterval = -filteredValues.timeInterval
        }
        break
    }
    if (editMode) {
      onUpdateSubmit(filteredValues)
    } else {
      onAddSubmit(filteredValues)
    }
  }

  useEffect(() => {
    if (!appContext.isSidebarOpen) {
      // formRef.current.resetForm()
      setSelectedReceiver([])
    }
  }, [appContext.isSidebarOpen])

  useEffect(() => {
    if (editItemResult.status === 2) {
      setSelectedReceiver(editItemResult.data?.recipients)
    }
  }, [editItemResult.status])

  const buttonFeedbackResult = editMode ? updateResult : addResult
  const [selectedReceivers, setSelectedReceiver] = useState(initialValues.recipients)

  const removeReceiver = (setFieldValue, values, index) => {
    let newReceivers = values.recipients
    newReceivers.splice(index, 1)
    setFieldValue("recipients", newReceivers)
  }

  return (
    <Sidebar
      initialFormikValues={initialValues}
      setInitialFormikValues={() => {}}
      validationSchema={validationSchema}
      formResult={buttonFeedbackResult}
      handleSubmit={handleSubmit}
      formRef={formRef}
      formData={{
        formId: "CustomFieldForm",
        icon: faDatabase,
        titleNew: t("global.add_new_field", "Add new field"),
        subTitleNew: t("global.create_your_own_field", "Create your own field"),
        titleEdit: `${t("global.edit", "Edit")} ${initialValues?.name}`,
        subTitleEdit: t("global.edit_field", "Edit field"),
        submitText: t("global.add_field", "Add field"),
        submitEditText: t("global.save", "Save")
      }}>
      <Box display="flex" flexDirection="column" gap={2}>
        <NameTextField />
        <NoteTextField />
        <Box>
          <MandatorySwitch />
          <ActiveSwitch />
        </Box>
        <TypeDropdown editMode={editMode} />
        <LastStep
          usersResult={usersResult}
          selectedReceivers={selectedReceivers}
          setSelectedReceiver={setSelectedReceiver}
          removeReceiver={removeReceiver}
        />
      </Box>
    </Sidebar>
  )
}

export default CustomFieldForm
